import { type ApolloError, useMutation } from "@apollo/client"
import cn from "classnames"
import React, { useState, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { graphql } from "@/api"
import ClientData, { type EditingState } from "@/common/client_data"
import SignPreview, { useSignPreview } from "@/common/components/sign_preview"
import env from "@/common/env"
import Sign from "@/signomatic/app/sign"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { InfoOutline } from "@/skylticons"

const exampleCreateDoc = graphql(`
  mutation SignCreate($signcode: String!) {
    signCreate(signcode: $signcode) {
      sign {
        id
      }
    }
  }
`)

const Divider = () => <div className="mb-4 border-b"></div>

const AdminToolbox: React.FC<ToolboxStripItemProps> = ({ sign }) => {
  const [addOrderState, setAddOrderState] = useState((ClientData.admin?.lastOrderId || "").toString())
  const editing = useSelector<State, EditingState | null>(state => state.editing)
  const dispatch = useDispatch()

  const adminPrefix = `//${env.adminHost}`
  const signwebLocaleParam = `signweb_locale=${env.locale}`

  const addToOrder = useCallback(() => {
    if (addOrderState.toLowerCase().startsWith("b")) {
      location.href = `${adminPrefix}/bundles/${addOrderState}?from=${ClientData.orderId}?${signwebLocaleParam}`
    } else {
      location.href = `/order/add_items_to_order?order_id=${addOrderState}`
    }
  }, [addOrderState, adminPrefix, signwebLocaleParam])

  const changeAddOrder = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddOrderState(e.target.value)
    },
    [setAddOrderState]
  )

  const signFonts = sign.allObjects.filter(obj => Sign.isText(obj.state)).map((obj: any) => obj.fontFamily)
  const customFont = sign.fontdata.sets.custom.some(font => signFonts.includes(font))

  const [exampleCreating, setExampleCreating] = useState(false)
  const [exampleMutation] = useMutation(exampleCreateDoc)

  const createExample = async () => {
    setExampleCreating(true)
    let signcode: string

    try {
      signcode = JSON.stringify(await sign.signcode())
    } catch (e) {
      console.error(e)
      alert(e)
      setExampleCreating(false)
      return
    }

    exampleMutation({
      variables: { signcode },
      onCompleted: data => {
        const newSign = data.signCreate?.sign

        if (newSign) {
          location.href = `${adminPrefix}/signs/${newSign.id}?${signwebLocaleParam}`
        }
      },
      onError: (error: ApolloError) => {
        setExampleCreating(false)

        if (error.graphQLErrors[0]) {
          return alert(error.graphQLErrors[0].message)
        }

        console.error(error)
        alert(error)
      },
    })
  }

  const [openPreview, previewProps] = useSignPreview()

  return (
    <Toolbox tKey="admin">
      <div className="admin-toolbox mx-4 my-6">
        <div className="mb-4 flex gap-1">
          <a
            id="order_link"
            className="button button-secondary mb-0 ml-0 flex-1"
            href={`${adminPrefix}/orders/${ClientData.orderId}?${signwebLocaleParam}`}
          >
            <InfoOutline />
            Go to order info
          </a>
        </div>
        <Divider />
        {ClientData.orderId && !editing && (
          <>
            <h5 className="mb-1">From basket</h5>
            <div className="mb-4 flex items-center gap-1">
              <a
                className="button button-secondary mb-0 ml-0 flex-1"
                href={`${adminPrefix}/orders/${ClientData.orderId}/convert_offer?${signwebLocaleParam}`}
              >
                Create quote
              </a>
              <a
                id="create_bundle"
                className="button button-secondary mb-0 ml-0 flex-1"
                href={`${adminPrefix}/bundles?from=${ClientData.orderId}&${signwebLocaleParam}`}
              >
                Create bundle
              </a>
            </div>
            <div className="form mb-4 flex flex-col">
              <label htmlFor="order_id" className="whitespace-nowrap">
                Move products to order or bundle
              </label>
              <div className="flex w-full">
                <input id="order_id" value={addOrderState} onChange={changeAddOrder} className="mr-1 w-full" />
                <a id="add_items_to_order_btn" className="button button-emphasis mb-0" onClick={addToOrder}>
                  OK
                </a>
              </div>
            </div>
            <Divider />
          </>
        )}
        <h5 className="mb-1">From current sign</h5>
        <div className="mb-4 flex gap-1">
          <button
            className="button button-secondary mb-0 ml-0 flex-1"
            onClick={() => dispatch(actions.openFontUpload())}
            type="button"
          >
            Upload font
          </button>
          <a
            id="create_example"
            className={cn("button button-secondary mb-0 ml-0 flex-1", {
              "disabled pointer-events-none": exampleCreating || customFont || !!editing,
            })}
            onClick={createExample}
          >
            Create example
          </a>
        </div>
        {ClientData.developer && (
          <>
            <SignPreview {...previewProps} />
            <Divider />
            <h5 className="mb-1">Exports</h5>
            <div className="mb-2 grid grid-cols-2">
              <button
                onClick={async () => openPreview({ svg: await sign.toSVG() })}
                className="button button-primary mb-0"
                type="button"
              >
                Preview
              </button>

              <button
                onClick={async () => openPreview({ svg: [await sign.toEngravingSVG(true)] })}
                className="button button-secondary mb-0"
                disabled={sign.production !== "laser"}
                type="button"
              >
                Engraving
              </button>
            </div>
            <h5 className="mb-1">Positioner test</h5>
            <div className="mb-2 grid grid-cols-3">
              <button
                onClick={() => {
                  const contents = ["text 1 " + Math.round(Math.random() * 9999)]
                  sign.importContents(contents)
                }}
                className="button button-secondary mb-0"
                type="button"
              >
                1 text
              </button>

              <button
                onClick={() => {
                  const contents = [
                    "text 1 texting me " + Math.round(Math.random() * 9999),
                    "text 2 texting me " + Math.round(Math.random() * 9999),
                  ]
                  sign.importContents(contents)
                }}
                className="button button-secondary mb-0"
                type="button"
              >
                2 texts
              </button>

              <button
                onClick={() => {
                  const contents = [
                    "text 1 " + Math.round(Math.random() * 9999),
                    "text 2 " + Math.round(Math.random() * 9999),
                    "text 3 " + Math.round(Math.random() * 9999),
                  ]
                  sign.importContents(contents)
                }}
                className="button button-secondary mb-0"
                type="button"
              >
                3 texts
              </button>
            </div>
            <h5 className="mb-1">Varia</h5>
            <div className="mb-4 flex">
              <button
                className="button button-secondary mb-0"
                onClick={() => document.body.classList.toggle("signomatic-debug")}
                type="button"
              >
                Toggle debug
              </button>

              <button
                className="button button-secondary mb-0"
                onClick={() => (sign.opentypeText = !sign.opentypeText)}
                type="button"
              >
                Toggle Opentype
              </button>
            </div>
          </>
        )}
      </div>
    </Toolbox>
  )
}

export default AdminToolbox
